import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClientAppShell from "../../components/ClientAppShell";
import ProfileInfo from "../components/profile-info";
import ContentLoader from "../../components/ContentLoader";
import ProjectSelectionScreen from "../../components/ProjectSelectionScreen";
import SmeProjectReportPanel from "../../components/sme/SmeProjectReportPanel";
import { Button, Alert } from "../../components/ui";
import { getSMEPendingProjectList } from "../../services/smeApi";
import { useAuth } from "../../auth/AuthContext";
import { confirmAlert } from "../../components/AppAlert/appAlert";
import { confirmAbandonReportGeneration } from "../../utils/reportGenerationGuard";
import { useSmeReportSessionNavigationGuard } from "../../hooks/useSmeReportSessionNavigationGuard";
import {
  dedupePendingProjectsByProject,
  getClientUserId,
  getCompanyName,
  getPendingProjectIdKey,
  getProjectId,
  readCachedSmePendingProjects,
  writeCachedSmePendingProjects,
  type SmePendingProject,
} from "../../utils/smeProjectUtils";
import "../../styles/sme-workspace.css";

const SmeProjectReports = () => {
  const { user } = useAuth();
  const [serverError, setServerError] = useState("");
  const [profile, setProfile] = useState<typeof user>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>(null);
  const [pendingProjects, setPendingProjects] = useState<SmePendingProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<SmePendingProject | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isCustomizingReport, setIsCustomizingReport] = useState(false);

  const handleAbandonReportSession = useCallback(() => {
    setIsGeneratingReport(false);
    setIsCustomizingReport(false);
  }, []);

  useSmeReportSessionNavigationGuard(
    isGeneratingReport,
    isCustomizingReport,
    handleAbandonReportSession
  );

  const clientUserId = useMemo(
    () => getClientUserId(selectedProject),
    [selectedProject]
  );
  const selectedProjectId = useMemo(
    () => getProjectId(selectedProject),
    [selectedProject]
  );

  useEffect(() => {
    document.body.classList.remove("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      const cached = readCachedSmePendingProjects();
      if (cached) {
        setPendingProjects(dedupePendingProjectsByProject(cached));
        setLoadingProjects(false);
        if (user) setProfile(user);
      } else {
        setLoadingProjects(true);
      }

      try {
        const result = await getSMEPendingProjectList();
        const raw = result?.data?.projects ?? result?.projects ?? [];
        const list = Array.isArray(raw) ? raw : [];
        setPendingProjects(dedupePendingProjectsByProject(list));
        writeCachedSmePendingProjects(list);

        if (user) setProfile(user);
      } catch (error) {
        console.error("Error fetching projects for reports:", error);
        setServerError(
          `Error fetching projects: ${error instanceof Error ? error.message : "Unknown error"}`
        );
        if (user) setProfile(user);
      } finally {
        setLoadingProjects(false);
      }
    };

    void fetchProjects();
  }, [user]);

  const confirmLeaveCustomizeSession = async (actionLabel: string) => {
    if (!isCustomizingReport) return true;
    const shouldLeave = await confirmAlert({
      type: "warning",
      title: "Leave customization?",
      message: `You have unsaved report changes. ${actionLabel}?`,
      confirmText: "Leave",
      cancelText: "Stay",
    });
    if (shouldLeave) {
      setIsCustomizingReport(false);
    }
    return shouldLeave;
  };

  const handleProjectSelect = async (project: SmePendingProject) => {
    if (isGeneratingReport) {
      const shouldLeave = await confirmAbandonReportGeneration("switch projects");
      if (!shouldLeave) return;
      setIsGeneratingReport(false);
    }
    if (!(await confirmLeaveCustomizeSession("Switching projects will discard them"))) {
      return;
    }
    setSelectedProject(project);
  };

  const handleBackToProjects = async () => {
    if (isGeneratingReport) {
      const shouldLeave = await confirmAbandonReportGeneration("leave this project");
      if (!shouldLeave) return;
      setIsGeneratingReport(false);
    }
    if (!(await confirmLeaveCustomizeSession("Leaving this project will discard them"))) {
      return;
    }
    setSelectedProject(null);
  };

  const renderProjectMeta = (project: SmePendingProject) =>
    [
      getCompanyName(project) && { label: "Company", value: getCompanyName(project) },
      project.departmentName && { label: "Department", value: project.departmentName },
      project.projectNumber && { label: "Project #", value: project.projectNumber },
    ].filter(Boolean) as Array<{ label: string; value: string }>;

  const renderProjectPicker = () => {
    if (loadingProjects) {
      return <ContentLoader compact label="Loading projects…" />;
    }

    const projects = pendingProjects.map((project) => ({
      id: getPendingProjectIdKey(project),
      name: project.projectName || "Untitled project",
      companyName: getCompanyName(project) || null,
      meta: renderProjectMeta(project).filter((row) => row.label !== "Company"),
    }));

    return (
      <ProjectSelectionScreen
        embedded
        pickerHeading="Projects ready for reports"
        title="Projects ready for reports"
        eyebrow={null}
        subtitle={null}
        projects={projects}
        onSelect={(id) => {
          const project = pendingProjects.find(
            (item) => getPendingProjectIdKey(item) === String(id)
          );
          if (project) {
            void handleProjectSelect(project);
          }
        }}
        emptyTitle="No projects available"
        emptyHint={
          user?.userId
            ? "Projects appear here once at least one team member has submitted an assessment."
            : "Projects will appear here after clients complete assessments."
        }
      />
    );
  };

  const workspaceMeta = [
    getCompanyName(selectedProject),
    selectedProject?.departmentName,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ClientAppShell
      title="Project Reports"
      dataNav="sme-project-reports"
      contentClassName="sme-workspace-page"
      selectedItem={selectedMenuItem}
      onMenuSelect={setSelectedMenuItem}
      profileSlot={
        <ProfileInfo profile={profile} currentUserId={user?.userId} />
      }
      intro={
        !selectedProject
          ? {
              eyebrow: "Capability assessment reports",
              description:
                "Reports are generated for the whole project. Select a project with completed assessments to generate, customize, and submit the client report.",
            }
          : undefined
      }
    >
      {!selectedProject ? (
        renderProjectPicker()
      ) : (
        <div className="sme-workspace">
          <section className="sme-workspace__shell" aria-label="Project report workspace">
            <header className="sme-workspace__header">
              <div className="sme-workspace__project">
                <p className="sme-workspace__eyebrow">Active project</p>
                <h2 className="sme-workspace__title">
                  {selectedProject.projectName || "Selected project"}
                </h2>
                {workspaceMeta ? (
                  <p className="sme-workspace__meta">{workspaceMeta}</p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="sme-workspace__back"
                onClick={() => void handleBackToProjects()}
              >
                Back to projects
              </Button>
            </header>

            <SmeProjectReportPanel
              key={`report-${selectedProjectId}-${clientUserId}`}
              project={selectedProject}
              clientUserId={clientUserId}
              onGeneratingChange={setIsGeneratingReport}
              onCustomizingChange={setIsCustomizingReport}
            />
          </section>

          {serverError ? (
            <div className="sme-workspace__footer">
              <Alert variant="error" description={serverError} />
            </div>
          ) : null}
        </div>
      )}
    </ClientAppShell>
  );
};

export default SmeProjectReports;
